import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { StudentCard } from './StudentCard';
import { Pagination } from '../UI/Pagination';
import { EmptyState } from '../UI/EmptyState';
import {
  StudentSearchResult,
  PaginationInfo,
} from '../../types/student-search';

interface StudentSearchResultsProps {
  students: StudentSearchResult[];
  pagination: PaginationInfo | null;
  loading: boolean;
  selectedStudents: number[];
  onStudentSelect: (studentId: number) => void;
  onStudentDetail: (studentId: number) => void;
  onPageChange: (page: number) => void;
  onBulkScout: (studentIds: number[]) => void;
  onSendScout: (studentId: number) => void;
  onClearSelection: () => void;
}

export const StudentSearchResults: React.FC<StudentSearchResultsProps> = ({
  students,
  pagination,
  loading,
  selectedStudents,
  onStudentSelect,
  onStudentDetail,
  onPageChange,
  onBulkScout,
  onSendScout,
  onClearSelection,
}) => {
  // ローディング状態
  if (loading) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          学生を検索中...
        </Typography>
      </Paper>
    );
  }

  // 初期状態（検索前）
  if (!pagination) {
    return (
      <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.paper' }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <svg
            width="80"
            height="80"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            style={{ color: '#9CA3AF' }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        </Box>
        <Typography
          variant="h5"
          sx={{
            mb: 2,
            color: 'text.primary',
            fontWeight: 600,
          }}
        >
          学生を検索してみましょう
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            maxWidth: 400,
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          上記の検索フォームに条件を入力して、
          <br />
          あなたの求める学生を見つけてください
        </Typography>
      </Paper>
    );
  }

  // 検索結果なし
  if (students.length === 0) {
    return (
      <Paper sx={{ p: 4 }}>
        <EmptyState
          title="条件に該当する学生が見つかりません"
          description="検索条件を変更して、再度お試しください"
          icon="📭"
        />
      </Paper>
    );
  }

  return (
    <Box>
      {/* 検索結果ヘッダー */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            検索結果
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pagination.total}件の学生が見つかりました
            {pagination.total_pages > 1 && (
              <span>
                （{pagination.page}/{pagination.total_pages}ページ）
              </span>
            )}
          </Typography>
        </Box>

        {/* 選択中の学生アクション */}
        {selectedStudents.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Chip
                label={`${selectedStudents.length}名選択中`}
                color="primary"
                variant="outlined"
                sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: 1, sm: 2 },
                  flexDirection: { xs: 'column', sm: 'row' },
                  flex: 1,
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => onBulkScout(selectedStudents)}
                  size="small"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  選択した学生にスカウト送信
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={onClearSelection}
                  size="small"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  選択解除
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* 学生カード一覧 */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {students.map((student) => (
          <Grid item xs={12} md={6} lg={4} key={student.id}>
            <StudentCard
              student={student}
              selected={selectedStudents.includes(student.id)}
              onSelect={() => onStudentSelect(student.id)}
              onViewDetails={() => onStudentDetail(student.id)}
              onSendScout={() => onSendScout(student.id)}
            />
          </Grid>
        ))}
      </Grid>

      {/* ページネーション */}
      {pagination.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.total_pages}
            onPageChange={onPageChange}
            showInfo={true}
            totalItems={pagination.total}
            itemsPerPage={20}
          />
        </Box>
      )}
    </Box>
  );
};
